import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, BarChart3, TrendingUp, Clock, Flame } from "lucide-react";
import api from "@/lib/api";
import AppUnavailable from "@/pages/AppUnavailable";

export default function KBUsage() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [days, setDays] = usePersistedState("kb-usage.days", 30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/kb/usage", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [days]);

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;
  const maxDay = data ? Math.max(1, ...data.views_by_day.map((d) => d.views)) : 1;

  const List = ({ items, right, testid }) => (
    <ul className="space-y-1.5" data-testid={testid}>
      {items.length === 0 && <li className="text-sm text-slate-400 dark:text-slate-500">Nothing yet.</li>}
      {items.map((a, i) => (
        <li key={i} className="flex items-center justify-between gap-2 text-sm">
          <button className="min-w-0 truncate text-left text-slate-700 dark:text-slate-200 hover:text-primary"
            onClick={() => a.slug && navigate(`/kb/a/${a.slug}`)}>
            {a.name}{a.is_draft && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] text-amber-700">Draft</span>}
          </button>
          <span className="ml-2 shrink-0 text-xs text-slate-400 dark:text-slate-500">{right(a)}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="mx-auto max-w-5xl" data-testid="kb-usage-page">
      <button onClick={() => navigate("/kb")} data-testid="kb-usage-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
      </button>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Usage Reporting</h1>
        </div>
        <div className="flex gap-1" data-testid="kb-usage-range">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`kb-usage-days-${d}`}
              className={`rounded-md border px-3 py-1 text-xs font-medium ${days === d ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>{d}d</button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      ) : (
        <>
          <section className="mb-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-usage-chart">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Views — last {data.days} days</h2>
              <span className="text-sm text-slate-500 dark:text-slate-400">{data.window_views.toLocaleString()} total</span>
            </div>
            {data.views_by_day.length === 0 ? (
              <p className="text-sm text-slate-400 dark:text-slate-500">No views recorded yet in this window. (Views start logging from now on.)</p>
            ) : (
              <div className="flex h-32 items-end gap-0.5">
                {data.views_by_day.map((d) => (
                  <div key={d.date} className="flex-1 rounded-t bg-primary/70 transition-all hover:bg-primary"
                    style={{ height: `${Math.max(4, (d.views / maxDay) * 100)}%` }} title={`${d.date}: ${d.views}`} />
                ))}
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100"><Flame className="h-4 w-4 text-orange-500" /> Trending (this window)</h2>
              <List items={data.trending} testid="kb-usage-trending" right={(a) => `${a.recent_views} views`} />
            </section>
            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100"><TrendingUp className="h-4 w-4 text-emerald-500" /> Most read (all time)</h2>
              <List items={data.top_all_time} testid="kb-usage-top" right={(a) => `${(a.hj_views || 0).toLocaleString()} views`} />
            </section>
            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 md:col-span-2">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100"><Clock className="h-4 w-4 text-rose-500" /> Popular but stale <span className="text-xs font-normal text-slate-400 dark:text-slate-500">— high views, oldest updates. Consider refreshing.</span></h2>
              <List items={data.stale_popular} testid="kb-usage-stale" right={(a) => `updated ${(a.updated_at || "").slice(0, 10) || "—"}`} />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
